import React from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import DataTable from './DataTable';
import {getCatalogArtists, getCatalogSongs} from '../../libraries/AdminApi';

const FALLBACK_COVER = '/images/cover-placeholder.jpg';
const Badge = ({children, red}) => (
    <span className={'flex-none rounded-full px-1.5 py-0.5 text-[8.5px] font-extrabold uppercase '
        + (red ? 'bg-apple-red/15 text-apple-red' : 'bg-white/10 text-white/60')}>{children}</span>
);
const familyOptions = (r) => r?.filters?.families || [];
const genreOptions = (r) => r?.filters?.genres || [];

/**
 * The catalog: every analyzed song (search, filters, sorting) and an artist
 * view. Clicking a song opens it in the Song Lab; clicking an artist lists
 * their songs.
 */
export default function CatalogPage({base, audio}) {
    const history = useHistory();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const view = query.get('view') === 'artists' ? 'artists' : 'songs';
    const artist = query.get('artist') || '';

    const go = (next) => {
        const qs = new URLSearchParams(Object.entries(next).filter(([, v]) => v));
        history.push(`${base}/catalog${qs.toString() ? '?' + qs : ''}`);
    };

    return (
        <div>
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h1 className="font-heading text-[28px] font-bold tracking-[-0.015em]">Catalog</h1>
                    <p className="mt-1 text-[13px] text-white/45">Every analyzed song. Click a song to open it in the Song Lab.</p>
                </div>
                <div className="flex rounded-xl bg-white/[0.06] p-1 text-[12.5px] font-semibold">
                    {[['songs', 'Songs'], ['artists', 'Artists']].map(([v, label]) => (
                        <button key={v} type="button" onClick={() => go({view: v === 'songs' ? '' : v})}
                                className={'rounded-lg px-4 py-1.5 transition ' + (view === v ? 'bg-white/[0.14] text-white' : 'text-white/50 hover:text-white')}>
                            {label}
                        </button>
                    ))}
                </div>
            </div>

            {view === 'songs' && artist && (
                <div className="mt-5 flex items-center gap-2 text-[12.5px]">
                    <span className="flex items-center gap-2 rounded-full bg-white/[0.10] py-1 pl-3 pr-1.5">
                        Artist: <b>{artist}</b>
                        <button type="button" onClick={() => go({})} className="flex h-5 w-5 items-center justify-center rounded-full text-white/50 hover:bg-white/10 hover:text-white">
                            <i className="fa-solid fa-xmark text-[9px]"/>
                        </button>
                    </span>
                </div>
            )}

            <div className="mt-5">
                {view === 'songs' ? (
                    <DataTable
                        key={'songs-' + artist}
                        fetcher={getCatalogSongs}
                        params={artist ? {artist} : {}}
                        perPage={50}
                        defaultSort={artist ? {key: 'year', dir: 'desc'} : {key: 'id', dir: 'desc'}}
                        searchPlaceholder="Search artist, title or album…"
                        onRowClick={(s) => history.push(`${base}/lab/${s.id}`)}
                        filters={[
                            {key: 'family', label: 'Family', options: familyOptions},
                            {key: 'genre', label: 'Genre', options: genreOptions},
                            {key: 'year_from', label: 'From year', type: 'number', width: 105},
                            {key: 'year_to', label: 'To year', type: 'number', width: 95},
                            {key: 'version', label: 'Version', options: [
                                {value: 'original', label: 'Originals'}, {value: 'live', label: 'Live'}, {value: 'remix', label: 'Remixes'}]},
                            {key: 'fingerprint', label: 'Fingerprint', options: [
                                {value: 'preview', label: 'Preview'}, {value: 'full', label: 'Full song'}]},
                        ]}
                        columns={[
                            {key: 'title', label: 'Song', sortable: true, render: (s) => (
                                <span className="flex min-w-[260px] items-center gap-3">
                                    <img src={s.cover || FALLBACK_COVER} alt="" loading="lazy" className="h-10 w-10 flex-none rounded-lg object-cover"/>
                                    <span className="min-w-0">
                                        <span className="flex items-center gap-1.5 font-semibold">
                                            <span className="truncate">{s.title}</span>
                                            {s.is_live && <Badge red>Live</Badge>}
                                            {s.is_remix && <Badge>Remix</Badge>}
                                            {s.has_full && <Badge>Full</Badge>}
                                        </span>
                                        <span className="block truncate text-[11.5px] text-white/45">{s.album || '—'}</span>
                                    </span>
                                </span>
                            )},
                            {key: 'artist', label: 'Artist', sortable: true, render: (s) => (
                                <button type="button" onClick={(e) => { e.stopPropagation(); go({artist: s.artist}); }}
                                        className="text-left text-white/75 hover:text-white hover:underline">{s.artist}</button>
                            )},
                            {key: 'genre', label: 'Genre', render: (s) => (
                                <span className="text-white/65">{s.genre || '—'}
                                    {s.family && <span className="block text-[10.5px] text-white/35">{s.family}</span>}</span>
                            )},
                            {key: 'year', label: 'Year', sortable: true, className: 'tabular-nums text-white/65'},
                            {key: 'isrc', label: 'ISRC', className: 'font-mono text-[11px] text-white/50'},
                            {key: 'id', label: 'ID', sortable: true, className: 'tabular-nums text-[11.5px] text-white/40'},
                        ]}
                        actions={(s) => (
                            <span className="flex justify-end gap-1.5">
                                {s.song_url && (
                                    <button type="button" onClick={() => audio.toggle(s.id, s.song_url)} title="Play the preview"
                                            className={'flex h-7 w-7 items-center justify-center rounded-full transition '
                                                + (audio.playing === s.id ? 'bg-apple-red' : 'bg-white/10 text-white/80 hover:bg-white/20')}>
                                        <i className={'fa-solid text-[10px] ' + (audio.playing === s.id ? 'fa-pause' : 'fa-play pl-0.5')}/>
                                    </button>
                                )}
                                <button type="button" onClick={() => history.push(`${base}/lab/${s.id}`)} title="Open in the Song Lab"
                                        className="flex h-7 items-center gap-1.5 rounded-full bg-white/10 px-2.5 text-[11px] font-semibold text-white/75 hover:bg-white/20">
                                    <i className="fa-solid fa-flask text-[10px]"/>Lab
                                </button>
                            </span>
                        )}
                    />
                ) : (
                    <DataTable
                        key="artists"
                        fetcher={getCatalogArtists}
                        perPage={50}
                        defaultSort={{key: 'songs', dir: 'desc'}}
                        searchPlaceholder="Search artists…"
                        onRowClick={(a) => go({artist: a.artist})}
                        filters={[
                            {key: 'family', label: 'Family', options: familyOptions},
                            {key: 'genre', label: 'Main genre', options: genreOptions},
                        ]}
                        columns={[
                            {key: 'artist', label: 'Artist', sortable: true, render: (a) => (
                                <span className="flex min-w-[240px] items-center gap-3">
                                    <img src={a.cover || FALLBACK_COVER} alt="" loading="lazy" className="h-10 w-10 flex-none rounded-full object-cover"/>
                                    <span className="truncate font-semibold">{a.artist}</span>
                                </span>
                            )},
                            {key: 'genre', label: 'Main genre', render: (a) => (
                                <span className="text-white/65">{a.genre || '—'}
                                    {a.family && <span className="block text-[10.5px] text-white/35">{a.family}</span>}</span>
                            )},
                            {key: 'songs', label: 'Songs', sortable: true, className: 'tabular-nums text-white/70'},
                            {key: 'albums', label: 'Albums', sortable: true, className: 'tabular-nums text-white/70'},
                            {key: 'first_year', label: 'First', sortable: true, className: 'tabular-nums text-white/55'},
                            {key: 'last_year', label: 'Latest', sortable: true, className: 'tabular-nums text-white/55'},
                        ]}
                    />
                )}
            </div>
        </div>
    );
}
